import React from 'react';
import { Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { formatHabitGateValue, isAtTopLevel, isHabitGateMet, IMyNetworkScoreSummaryResponse } from '../../interfaces/INetworkScore';

interface NetworkScoreChecklistProps {
    summary: IMyNetworkScoreSummaryResponse;
}

/**
 * The "to reach <next level>" caption and checklist. Every row — the points threshold and each of
 * the API's `nextLevelHabitGates` — gets a met/unmet icon, decided by comparing the gate's own
 * `currentValue`/`targetValue`.
 */
export const NetworkScoreChecklist: React.FC<NetworkScoreChecklistProps> = ({ summary }) => {
    const atTopLevel = isAtTopLevel(summary);
    const pointsMet = summary.windowPoints >= summary.nextLevelThreshold;

    return (
        <>
            {!atTopLevel && (
                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', color: 'primary.main' }}>
                    TO REACH {summary.nextLevelName.toUpperCase()}
                </Typography>
            )}
            <Stack spacing={1} sx={{ mt: 1 }}>
                {!atTopLevel && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                            {pointsMet ? (
                                <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main', flexShrink: 0 }} />
                            ) : (
                                <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: 'text.disabled', flexShrink: 0 }} />
                            )}
                            <Typography variant="body2">{summary.nextLevelThreshold} points</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{summary.windowPoints}</Typography>
                    </Stack>
                )}
                {summary.nextLevelHabitGates.map((gate) => {
                    const gateMet = !atTopLevel && isHabitGateMet(gate);
                    return (
                        <Stack key={gate.habitGate} direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                {!atTopLevel && (gateMet ? (
                                    <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main', flexShrink: 0 }} />
                                ) : (
                                    <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: 'text.disabled', flexShrink: 0 }} />
                                ))}
                                <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{gate.habitGate}</Typography>
                            </Stack>
                            {!atTopLevel && <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{formatHabitGateValue(gate)}</Typography>}
                        </Stack>
                    );
                })}
            </Stack>
        </>
    );
};

export default NetworkScoreChecklist;
